import { useState, type ReactElement } from 'react';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { formatCurrency } from 'app/utils';
import useLoggedInUser from 'app/hooks/useLoggedInUser';
import bookConsultationUrl from 'app/utils/bookConsultationUrl';
import { DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';

import type { BillingCycle, SubscriptionPricing } from '../../../../hooks/useSubscription';
import { DEPLOYMENT_FEE, DEPLOYMENT_FEE_CURRENCY } from '../../../../hooks/useSubscription';

const BUILD_FEE_WAIVER_END_MS = new Date(2026, 9, 1).getTime();

interface UpgradeFormProps {
  isAdmin: boolean;
  isSubmitting?: boolean;
  pricing: SubscriptionPricing;
  onUpgrade?: (data: { billing: BillingCycle; goLiveDate?: Date }) => void;
}

export function UpgradeForm({
  isAdmin,
  isSubmitting = false,
  pricing,
  onUpgrade,
}: UpgradeFormProps): ReactElement {
  const user = useLoggedInUser();
  const [billing, setBilling] = useState<BillingCycle>('annual');
  const [goLiveDate, setGoLiveDate] = useState<Date | undefined>();
  const [calendarOpen, setCalendarOpen] = useState(false);

  const isAnnual = billing === 'annual';
  const annualMonthlyRate = pricing.annual / 12;
  const annualSaving = pricing.monthly * 12 - pricing.annual;
  const { currency } = pricing;

  let pricingContent: ReactElement;
  if (isAnnual) {
    pricingContent = (
      <>
        <p className="vtwo:text-lg vtwo:font-semibold">
          {formatCurrency(annualMonthlyRate, currency)}/month per pharmacy site
        </p>
        <p className="vtwo:text-sm vtwo:text-gray-600">{pricing.annualFormatted} billed annually</p>
        {annualSaving > 0 && (
          <p className="vtwo:text-sm vtwo:font-semibold vtwo:text-green-600">
            Save {formatCurrency(annualSaving, currency)}
          </p>
        )}
      </>
    );
  } else {
    pricingContent = (
      <>
        <p className="vtwo:text-lg vtwo:font-semibold">
          {pricing.monthlyFormatted}/month per pharmacy site
        </p>
      </>
    );
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const isBuildFeeWaived = Date.now() < BUILD_FEE_WAIVER_END_MS;

  const actionSection = isAdmin ? (
    <>
      <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            data-empty={!goLiveDate}
            className={cn(
              'vtwo:w-full vtwo:justify-between vtwo:text-left vtwo:font-normal',
              'vtwo:data-[empty=true]:text-muted-foreground'
            )}
          >
            {goLiveDate ? format(goLiveDate, 'PPP') : 'Go-live date preferred (optional)'}
            <CalendarIcon aria-hidden="true" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="vtwo:w-auto vtwo:p-0 vtwo:z-[60] vtwo:pointer-events-auto"
          align="start"
        >
          <Calendar
            mode="single"
            selected={goLiveDate}
            defaultMonth={goLiveDate}
            disabled={(date) => date < today}
            onSelect={(date) => {
              setGoLiveDate(date);
              setCalendarOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>

      <Button
        size="xl"
        disabled={isSubmitting}
        onClick={() => onUpgrade?.({ billing, goLiveDate })}
        className="vtwo:w-full vtwo:bg-featured vtwo:hover:bg-featured-hover vtwo:rounded-full"
      >
        {isSubmitting ? 'Upgrading…' : 'Upgrade website'}
      </Button>
    </>
  ) : (
    <Button variant="outline" size="xl" disabled className="vtwo:w-full">
      Contact your administrator to upgrade.
    </Button>
  );

  return (
    <>
      <div>
        <DialogTitle className="vtwo:text-2xl vtwo:font-bold vtwo:text-center">
          Upgrade your account
        </DialogTitle>
        <DialogDescription className="vtwo:mt-2 vtwo:text-center" hidden>
          Benefit message TBD
        </DialogDescription>
      </div>

      <div className="vtwo:flex vtwo:justify-center">
        <ToggleGroup
          type="single"
          value={billing}
          onValueChange={(val) => {
            if (val) setBilling(val as BillingCycle);
          }}
          className="vtwo:bg-gray-100 vtwo:p-1"
        >
          <ToggleGroupItem
            value="annual"
            className="vtwo:px-6 vtwo:py-3 vtwo:min-w-min vtwo:text-sm vtwo:font-medium vtwo:data-[state=on]:bg-featured vtwo:data-[state=on]:text-white vtwo:data-[state=on]:hover:bg-featured-hover"
          >
            Annual (recommended)
          </ToggleGroupItem>
          <ToggleGroupItem
            value="monthly"
            className="vtwo:px-6 vtwo:py-3 vtwo:text-sm vtwo:font-medium vtwo:data-[state=on]:bg-featured vtwo:data-[state=on]:text-white vtwo:data-[state=on]:hover:bg-featured-hover"
          >
            Monthly
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      <div className="vtwo:text-center vtwo:flex vtwo:flex-col vtwo:gap-1">
        {pricingContent}
        {!isAdmin && (
          <p className="vtwo:text-sm vtwo:text-gray-500">
            {isBuildFeeWaived ? (
              <>
                <s>{formatCurrency(DEPLOYMENT_FEE, DEPLOYMENT_FEE_CURRENCY)}</s> build fee waived
                until 30 September 2026
              </>
            ) : (
              <>+ {formatCurrency(DEPLOYMENT_FEE, DEPLOYMENT_FEE_CURRENCY)} build fee</>
            )}
          </p>
        )}
      </div>

      <hr className="vtwo:border-gray-200" />

      {actionSection}

      <a
        href={bookConsultationUrl(user?.username)}
        target="_blank"
        rel="noopener noreferrer"
        className="vtwo:text-sm vtwo:font-medium vtwo:underline vtwo:text-center vtwo:cursor-pointer focus-ring vtwo:rounded-full vtwo:self-center vtwo:px-2 vtwo:py-1"
      >
        Contact sales
      </a>
    </>
  );
}
